import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, LogOut } from 'lucide-react'; // Make sure lucide-react is installed
import logo from '../../assets/images/inovaa.png';


const SIDEBAR_COLLAPSED = 60;
const SIDEBAR_EXPANDED  = 224;


const MIDEAST_THEME = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&family=Bebas+Neue&display=swap');


  /* ── RESET ───────────────────────────────────────────── */
  *, *::before, *::after { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { background: #080C18; font-family: 'DM Sans', sans-serif; color: #E2E8F0; }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.10); border-radius: 4px; }


  /* ── ROOT LAYOUT ─────────────────────────────────────── */
  .md-root {
    display: flex;
    width: 100%;
    min-height: 100vh;
    background: #080C18;
  }


  /* ── SIDEBAR ─────────────────────────────────────────── */
  .md-sidebar {
    position: fixed;
    top: 0; left: 0; bottom: 0;
    width: ${SIDEBAR_COLLAPSED}px;
    background: #0D1120;
    border-right: 1px solid rgba(255,255,255,0.055);
    box-shadow: 4px 0 24px rgba(0,0,0,0.32);
    display: flex;
    flex-direction: column;
    z-index: 100;
    overflow: hidden;
    transition: width 0.25s cubic-bezier(0.4,0,0.2,1);
  }
  .md-sidebar:hover {
    width: ${SIDEBAR_EXPANDED}px;
  }


  /* Logo */
  .md-sidebar-logo {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px 13px 16px;
    flex-shrink: 0;
    overflow: hidden;
    white-space: nowrap;
  }
  .md-logo-icon {
    width: 34px; height: 34px;
    border-radius: 10px;
    background: linear-gradient(135deg, #6D28D9, #DB2777, #F97316);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Bebas Neue', sans-serif;
    font-size: 18px; color: #fff;
    flex-shrink: 0;
    box-shadow: 0 0 14px rgba(219,39,119,0.35);
  }
  .md-logo-text {
    display: flex; flex-direction: column;
    overflow: hidden;
  }
  .md-logo-name {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 20px; letter-spacing: 0.12em;
    background: linear-gradient(90deg, #0EA5E9, #F59E0B);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1;
  }
  .md-logo-img { height: 24px; width: auto; object-fit: contain; display: block; }
  .md-logo-sub {
    font-family: 'DM Mono', monospace;
    font-size: 8px; letter-spacing: 0.22em;
    color: rgba(245,158,11,0.40);
    text-transform: uppercase;
    margin-top: 2px;
  }


  /* Divider */
  .md-sidebar-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent);
    margin: 4px 12px;
    flex-shrink: 0;
  }


  /* Nav list */
  .md-nav-list {
    list-style: none;
    margin: 8px 0 0;
    padding: 0;
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
  }
  .md-nav-list li { padding: 0; }


  /* Nav item */
  .md-nav-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 9px 13px;
    margin: 2px 8px;
    border-radius: 10px;
    border: 1px solid transparent;
    color: rgba(255,255,255,0.42);
    font-family: 'DM Sans', sans-serif;
    font-size: 13px; font-weight: 500;
    text-decoration: none;
    cursor: pointer;
    background: none;
    width: calc(100% - 16px);
    transition: all 0.18s ease;
    white-space: nowrap;
    overflow: hidden;
    position: relative;
  }
  .md-nav-item:hover {
    background: rgba(255,255,255,0.055);
    color: rgba(255,255,255,0.82);
    border-color: transparent;
  }
  .md-nav-item--active {
    background: linear-gradient(135deg, rgba(14,165,233,0.16), rgba(245,158,11,0.10)) !important;
    color: #E2E8F0 !important;
    border-color: rgba(14,165,233,0.22) !important;
    font-weight: 600 !important;
  }
  .md-nav-item--active .md-nav-icon {
    color: #0EA5E9 !important;
  }
  .md-nav-item--active::before {
    content: '';
    position: absolute;
    left: -8px; top: 6px; bottom: 6px;
    width: 3px;
    border-radius: 0 3px 3px 0;
    background: linear-gradient(180deg, #0EA5E9, #F59E0B);
  }


  .md-nav-icon {
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    width: 20px;
    color: rgba(255,255,255,0.28);
    transition: color 0.18s;
  }
  .md-nav-item:hover .md-nav-icon { color: rgba(255,255,255,0.65); }


  .md-nav-label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
  }
 
  /* Logout button */
  .md-sidebar-bottom {
    flex-shrink: 0;
    padding-bottom: 12px;
  }
  .md-nav-logout {
    color: rgba(255,255,255,0.28) !important;
  }
  .md-nav-logout:hover {
    background: rgba(239,68,68,0.08) !important;
    color: rgba(248,113,113,0.80) !important;
    border-color: rgba(239,68,68,0.15) !important;
  }
  .md-nav-logout:hover .md-nav-icon { color: rgba(248,113,113,0.70) !important; }


  /* ── MAIN CONTENT ────────────────────────────────────── */
  .md-main {
    margin-left: ${SIDEBAR_COLLAPSED}px;
    width: calc(100% - ${SIDEBAR_COLLAPSED}px);
    min-height: 100vh;
    background: #080C18;
    overflow-x: hidden;
    overflow-y: auto;
    height: 100vh;
    transition: margin-left 0.25s cubic-bezier(0.4,0,0.2,1),
                width      0.25s cubic-bezier(0.4,0,0.2,1);
  }


  /* ── TOP BAR ─────────────────────────────────────────── */
  .md-topbar {
    position: sticky; top: 0; z-index: 50;
    height: 56px;
    background: rgba(13,17,32,0.90);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(255,255,255,0.055);
    display: flex; align-items: center;
    padding: 0 28px;
    gap: 16px;
  }
  .md-topbar-title {
    flex: 1;
    font-size: 13px; font-weight: 700;
    color: rgba(255,255,255,0.30);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-family: 'DM Mono', monospace;
  }
  .md-topbar-title span {
    color: rgba(255,255,255,0.70);
  }
  .md-topbar-user {
    display: flex; align-items: center; gap: 10px;
  }
  .md-topbar-avatar {
    width: 30px; height: 30px;
    border-radius: 8px;
    background: linear-gradient(135deg, #0EA5E9, #F59E0B);
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 800; color: #fff;
    flex-shrink: 0;
  }
  .md-topbar-name {
    font-size: 12px; font-weight: 600;
    color: rgba(255,255,255,0.55);
  }
  .md-topbar-badge {
    font-family: 'DM Mono', monospace;
    font-size: 8px; letter-spacing: 0.14em;
    color: rgba(34,197,94,0.70); /* Green for customer */
    background: rgba(34,197,94,0.08);
    border: 1px solid rgba(34,197,94,0.18);
    border-radius: 5px;
    padding: 2px 7px;
    text-transform: uppercase;
  }


  /* ── PAGE CONTENT ────────────────────────────────────── */
  .md-page {
    padding: 28px;
    min-height: calc(100vh - 56px);
  }
`;


const MideastCustomerLayout = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const mainRef   = useRef(null);
  const [user, setUser] = useState(null);


  // Auth guard specific for customer
  useEffect(() => {
    const token = localStorage.getItem('mideast_token');
    const userData = localStorage.getItem('mideast_user');


    if (!token) {
      navigate('/me-login');
      return;
    }


    if (userData) {
      const parsed = JSON.parse(userData);
      // Ensure only customers can access this layout
      if (parsed.type !== 'mideast-customer') {
        navigate('/me-login');
        return;
      }
      setUser(parsed);
    }
  }, [navigate]);


  const getInitials = useCallback((u) => {
    if (!u) return 'C';
    return `${u.firstname?.[0] || ''}${u.lastname?.[0] || ''}`.toUpperCase() || 'C';
  }, []);


  const handleLogout = () => {
    localStorage.removeItem('mideast_token');
    localStorage.removeItem('mideast_user');
    navigate('/me-login');
  };


  if (!user) return null;


  const currentPath = location.pathname;


  return (
    <>
      <style>{MIDEAST_THEME}</style>


      <div className="md-root">
        {/* Sidebar (Inline for Customer) */}
        <div className="md-sidebar">
          <div className="md-sidebar-logo">
            <div className="md-logo-icon">i</div>
            <div className="md-logo-text">
              <img src={logo} alt="iNOVAA" className="md-logo-img" />
              <div className="md-logo-sub">Client Portal</div>
            </div>
          </div>
         
          <div className="md-sidebar-divider" />
         
          <ul className="md-nav-list">
            <li>
              <button
                className={`md-nav-item ${currentPath.includes('/me/dashboard') ? 'md-nav-item--active' : ''}`}
                onClick={() => navigate('/me/dashboard')}
              >
                <span className="md-nav-icon"><LayoutDashboard size={18} /></span>
                <span className="md-nav-label">Dashboard</span>
              </button>
            </li>
          </ul>
         
          <div className="md-sidebar-bottom">
            <button className="md-nav-item md-nav-logout" onClick={handleLogout}>
              <span className="md-nav-icon"><LogOut size={18} /></span>
              <span className="md-nav-label">Logout</span>
            </button>
          </div>
        </div>


        {/* Main */}
        <div className="md-main" ref={mainRef}>


          {/* Top bar */}
          <div className="md-topbar">
            <div className="md-topbar-title">
              iNOVAA / <span>Customer Portal</span>
            </div>
            <div className="md-topbar-user">
              <span className="md-topbar-badge">Client</span>
              <span className="md-topbar-name">
                {user.firstname} {user.lastname}
              </span>
              <div className="md-topbar-avatar">
                {getInitials(user)}
              </div>
            </div>
          </div>


          {/* Page content rendered by child routes (e.g. MECustomerDashboard) */}
          <div className="md-page">
            <Outlet />
          </div>


        </div>
      </div>
    </>
  );
};


export default MideastCustomerLayout;
